import { useEffect, useRef, useState } from 'react';

import AddFavorite from 'components/AddFavorite';
import { getFancyboxButtonShareTpl } from 'components/FancyboxButtons/FancyboxButtonShare';
import SharingPopup from 'components/SharingPopup';

import APIHandler from 'config/APIHandler';
import { appContentUrl, imgixUrl } from 'config/settings.json';

import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import { renderToString } from 'react-dom/server';
import { BeatLoader } from 'react-spinners';

export interface TrainingImageProps {
  appId: number;
  contentComplete: () => void;
  image: string;
  chapterTitle?: string;
  // credits?: string;
  imageCredits?: string; // TODO: <-- Clear duplicate values. imageCredits appears to be the correct one on APIHandler.getTrainingPages(someChapterId), 'en'.
  // image_credits?: string;
  pageID?: string;
  title?: string;
  type?: string;
  upID?: string;
}

interface FancyOptions {
  infobar: boolean;
  thumbs: { autoStart: boolean; axis: string };
  baseClass: string;
  buttons: string[];
  share?: { tpl: string };
  btnTpl?: { share: string };
}

function buildTrainingImageDownloadName(
  image: string,
  creditsRaw: string,
  title: string,
  chapterTitle: string,
): string {
  const pathOnly = (image || '').toString().split('?')[0] ?? '';
  const leaf = pathOnly.split('/').pop() ?? 'image';
  const leafWithoutExt = leaf.replace(/\.[^./]+$/, '');

  const label = creditsRaw.toString().trim() || title.toString().trim() || chapterTitle.toString().trim();

  if (label) {
    const safe = label
      .replace(/\s+/g, '_')
      .replace(/&/g, '_')
      .replace(/[/\\?%*:|"<>]/g, '');
    return safe.replace(/\.[a-zA-Z0-9]{2,5}$/, '');
  }
  return leafWithoutExt;
}

export function TrainingImage({
  appId,
  contentComplete,
  image,
  chapterTitle = '',
  // credits = '',
  imageCredits = '',
  // image_credits = '',
  pageID = '',
  title = '',
  type = '',
  upID = '',
}: TrainingImageProps) {
  const disabledImageShare = useFeatureFlag('disabled_image_share');
  const [loading, setLoading] = useState(true);
  const [href, setHref] = useState<string | null>(null);
  // Ref avoids stale closure in the onLoad handler
  const scrolledRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      scrolledRef.current = true;
    };
    document.addEventListener('scroll', onScroll, { once: true });
    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  const creditsForName = imageCredits.toString().trim();

  const downloadName = buildTrainingImageDownloadName(image, creditsForName, title, chapterTitle);
  const assetUrl = `${appContentUrl}${appId}/${image}`;

  const fancyOptions: FancyOptions = {
    infobar: true,
    thumbs: { autoStart: true, axis: 'x' },
    baseClass: 'customBox',
    buttons: disabledImageShare ? ['close'] : ['share', 'close'],
  };

  if (!disabledImageShare) {
    fancyOptions.share = {
      tpl: renderToString(
        <SharingPopup url={assetUrl} title={downloadName} image={`${imgixUrl}${appId}/${image}?quality=70&fm=jpg`} />,
      ),
    };
    fancyOptions.btnTpl = { share: getFancyboxButtonShareTpl() };
  }

  return (
    <div className={`trainingContent__image ${type.toLowerCase()}`}>
      <div
        className="trainingContent__image--wrapper"
        style={{ backgroundImage: `url(https://front.travpromobile.com/${appId}/final-header.png)` }}
      >
        <span className="loadingSpinner" style={{ opacity: loading ? 0 : 1 }}>
          {imageCredits}
        </span>
        {loading && <BeatLoader color="#aaa" loading={loading} />}
        <img
          className={`${type.toLowerCase()} ${type === 'Photo' ? 'photoType' : ''}`}
          data-fancybox={`trainingImage_${image}`}
          data-options={JSON.stringify(fancyOptions)}
          data-caption={imageCredits}
          src={`${imgixUrl}${appId}/${image}?quality=70&fm=jpg`}
          style={{ opacity: loading ? 0 : 1 }}
          alt={image.toString()}
          {...(href ? ({ href } as Record<string, unknown>) : {})}
          onLoad={() => {
            setLoading(false);
            setHref(APIHandler.getDownloadURL(assetUrl, downloadName));
            if (!scrolledRef.current) window.scrollTo(0, 0);
            contentComplete();
          }}
        />
        {imageCredits && <h3 className={clsx('global__text--title')}>{imageCredits}</h3>}
        <span className="zoomIcon">
          <FontAwesomeIcon icon={['far', 'search-plus']} />
        </span>
        {type === 'Photo' && <AddFavorite appId={appId} viewType="p" upID={upID} pageID={pageID} />}
      </div>
    </div>
  );
}
